import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UserBalanceSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function UserBalanceSubTab() {
  const [balances, setBalances] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingBalance, setEditingBalance] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    stars_earned_today: 0,
    balance: 0,
    tickets_balance: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [balancesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/user-balance`),
        axios.get(`${API_URL}/api/users`)
      ]);
      setBalances(balancesRes.data);
      setUsers(usersRes.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке данных: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        user_id: parseInt(formData.user_id),
        stars_earned_today: parseInt(formData.stars_earned_today) || 0,
        balance: parseInt(formData.balance) || 0,
        tickets_balance: parseInt(formData.tickets_balance) || 0
      };
      await axios.post(`${API_URL}/api/user-balance`, payload);
      setShowCreateForm(false);
      resetForm();
      fetchData();
    } catch (err) {
      setError('Ошибка при создании баланса: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdate = async (id) => {
    try {
      const payload = {
        user_id: parseInt(formData.user_id),
        stars_earned_today: parseInt(formData.stars_earned_today) || 0,
        balance: parseInt(formData.balance) || 0,
        tickets_balance: parseInt(formData.tickets_balance) || 0
      };
      await axios.put(`${API_URL}/api/user-balance/${id}`, payload);
      setEditingBalance(null);
      resetForm();
      fetchData();
    } catch (err) {
      setError('Ошибка при обновлении баланса: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот баланс?')) {
      try {
        await axios.delete(`${API_URL}/api/user-balance/${id}`);
        fetchData();
      } catch (err) {
        setError('Ошибка при удалении баланса: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleEdit = (balance) => {
    setEditingBalance(balance.id);
    setFormData({
      user_id: balance.user_id.toString(),
      stars_earned_today: balance.stars_earned_today.toString(),
      balance: balance.balance.toString(),
      tickets_balance: balance.tickets_balance.toString()
    });
    setShowCreateForm(false);
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      stars_earned_today: 0,
      balance: 0,
      tickets_balance: 0
    });
    setEditingBalance(null);
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingBalance(null);
    resetForm();
  };

  const getUserInfo = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `ID: ${user.id}, Telegram: ${user.telegram_id}` : `ID: ${userId}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="user-balance-sub-tab">
      <div className="tab-header">
        <h2>Баланс пользователей</h2>
        <button className="btn btn-primary" onClick={() => {
          resetForm();
          setShowCreateForm(true);
          setEditingBalance(null);
        }}>
          + Создать баланс
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {(showCreateForm || editingBalance) && (
        <div className="balance-form">
          <h3>{editingBalance ? 'Редактировать баланс' : 'Создать баланс'}</h3>
          <div className="form-group">
            <label>Пользователь *:</label>
            <select
              value={formData.user_id}
              onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
              required
              disabled={editingBalance !== null}
            >
              <option value="">Выберите пользователя</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  ID: {user.id}, Telegram: {user.telegram_id} ({user.category})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Звезд заработано за день:</label>
            <input
              type="number"
              value={formData.stars_earned_today}
              onChange={(e) => setFormData({ ...formData, stars_earned_today: e.target.value })}
              min="0"
              placeholder="0"
            />
          </div>
          <div className="form-group">
            <label>Баланс (звезды):</label>
            <input
              type="number"
              value={formData.balance}
              onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
              min="0"
              placeholder="0"
            />
          </div>
          <div className="form-group">
            <label>Баланс тикетов:</label>
            <input
              type="number"
              value={formData.tickets_balance}
              onChange={(e) => setFormData({ ...formData, tickets_balance: e.target.value })}
              min="0"
              placeholder="0"
            />
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" onClick={editingBalance ? () => handleUpdate(editingBalance) : handleCreate}>
              {editingBalance ? 'Сохранить' : 'Создать'}
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Отмена
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Пользователь</th>
              <th>Звезд за день</th>
              <th>Баланс (звезды)</th>
              <th>Баланс тикетов</th>
              <th>Последнее обновление</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {balances.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  Нет записей баланса. Создайте первую запись.
                </td>
              </tr>
            ) : (
              balances.map((balance) => (
                <tr key={balance.id}>
                  <td>{balance.id}</td>
                  <td>{getUserInfo(balance.user_id)}</td>
                  <td>{balance.stars_earned_today}</td>
                  <td className="balance-value">{balance.balance}</td>
                  <td className="tickets-value">{balance.tickets_balance}</td>
                  <td>{formatDate(balance.last_updated)}</td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(balance)}
                      title="Редактировать"
                    >
                      ✎
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(balance.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UserBalanceSubTab;
